import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Product } from '../types/store';
import { PRODUCTS as DEFAULT_PRODUCTS } from '../data/products';
import { findProductBySlugOrParam } from '../lib/urlHelpers';
import {
  subscribeProducts,
  saveProductDb,
  updateProductFieldDb,
  deleteProductDb,
  resetCatalogDb,
  getLocalCachedProducts,
  getDeletedProductIds,
  hydrateProductWithLiveAssets,
} from '../lib/firebase';

interface ProductsContextType {
  products: Product[];
  isLoading: boolean;
  error: string | null;
  addProduct: (product: Omit<Product, 'id'> | Product) => Promise<void>;
  updateProduct: (product: Product) => Promise<void>;
  updateProductField: (productId: string, partialFields: Partial<Product>) => Promise<void>;
  deleteProduct: (productId: string, imageUrls?: string[]) => Promise<void>;
  resetToDefaults: () => Promise<void>;
  getProductById: (id: string) => Product | undefined;
  getProductByFlavor: (flavor: string) => Product | undefined;
}

const ProductsContext = createContext<ProductsContextType | undefined>(undefined);

// Helper to filter deleted products and ensure proper asset hydration without resurrecting deleted items
function resolveActiveProducts(incoming: Product[]): Product[] {
  const deletedIds = getDeletedProductIds();

  if (Array.isArray(incoming) && incoming.length > 0) {
    return incoming
      .filter((p) => p && p.id && !deletedIds.includes(p.id))
      .map(hydrateProductWithLiveAssets);
  }

  return DEFAULT_PRODUCTS
    .filter((p) => !deletedIds.includes(p.id))
    .map(hydrateProductWithLiveAssets);
}

export const ProductsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>(() => resolveActiveProducts(getLocalCachedProducts()));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Dynamically fetch and subscribe to real-time updates from Firestore
    const unsubscribe = subscribeProducts(
      (updatedProducts) => {
        if (updatedProducts && updatedProducts.length > 0) {
          const resolved = resolveActiveProducts(updatedProducts);
          setProducts(resolved);
        } else {
          const resolved = resolveActiveProducts([]);
          setProducts(resolved);
        }
        setIsLoading(false);
      },
      (err) => {
        console.warn('Firestore real-time sync notice:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const addProduct = async (productData: Omit<Product, 'id'> | Product) => {
    const id = 'id' in productData && productData.id ? productData.id : `malrich-${Date.now()}`;
    const newProduct: Product = {
      ...productData,
      id,
      slug:
        productData.slug ||
        productData.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, ''),
      inStock: productData.inStock !== false,
    } as Product;

    // Instant optimistic update (0ms delay)
    setProducts((prev) => {
      const updated = [newProduct, ...prev.filter((p) => p.id !== newProduct.id)];
      return updated;
    });

    try {
      await saveProductDb(newProduct);
    } catch (err: any) {
      console.warn('Background product save notice:', err);
    }
  };

  const updateProduct = async (product: Product) => {
    // Instant optimistic update (0ms delay)
    setProducts((prev) => {
      const index = prev.findIndex((p) => p.id === product.id);
      const updated = index >= 0 ? [...prev] : [...prev, product];
      if (index >= 0) updated[index] = product;
      return updated;
    });

    try {
      await saveProductDb(product);
    } catch (err: any) {
      console.warn('Background product update notice:', err);
    }
  };

  const updateProductField = async (productId: string, partialFields: Partial<Product>) => {
    // Instant optimistic update (0ms delay)
    setProducts((prev) => {
      const index = prev.findIndex((p) => p.id === productId);
      if (index < 0) return prev;
      const updated = [...prev];
      updated[index] = { ...updated[index], ...partialFields };
      return updated;
    });

    try {
      await updateProductFieldDb(productId, partialFields);
    } catch (err: any) {
      console.warn('Background product field update notice:', err);
    }
  };

  const deleteProduct = async (productId: string, imageUrls?: string[]) => {
    // Instant optimistic update (0ms delay)
    setProducts((prev) => prev.filter((p) => p.id !== productId));

    try {
      await deleteProductDb(productId, imageUrls);
    } catch (err: any) {
      console.warn('Background product delete notice:', err);
    }
  };

  const resetToDefaults = async () => {
    await resetCatalogDb();
  };

  const getProductById = (id: string): Product | undefined => {
    return findProductBySlugOrParam(products, id);
  };

  const getProductByFlavor = (flavor: string): Product | undefined => {
    return findProductBySlugOrParam(products, flavor);
  };

  return (
    <ProductsContext.Provider
      value={{
        products,
        isLoading,
        error,
        addProduct,
        updateProduct,
        updateProductField,
        deleteProduct,
        resetToDefaults,
        getProductById,
        getProductByFlavor,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};
